import { HanamiElement, createShadow, define } from '../core/base';
import { getIcon, onIconRegistered } from '../icons';

const css = `:host{display:inline-block;width:1em;height:1em;flex-shrink:0;line-height:0;color:inherit;vertical-align:-0.125em}:host([hidden]){display:none}svg{display:block;width:100%;height:100%;overflow:visible}`;

/**
 * <hn-icon name="sakura" label="Fleur"></hn-icon>
 * Sans `label`, l'icône est décorative (masquée aux lecteurs d'écran).
 */
export class HnIcon extends HanamiElement {
  static readonly tagName = 'hn-icon';
  static get observedAttributes() {
    return ['name', 'label', 'size', 'stroke-width'];
  }

  private unsubscribe?: () => void;

  get name(): string {
    return this.getAttribute('name') ?? '';
  }
  set name(value: string) {
    this.setAttribute('name', value);
  }

  get label(): string {
    return this.getAttribute('label') ?? '';
  }
  set label(value: string) {
    if (value) this.setAttribute('label', value);
    else this.removeAttribute('label');
  }

  connectedCallback(): void {
    this.upgradeProperties('name', 'label');
    this.render();
    this.unsubscribe = onIconRegistered(name => {
      if (name === this.name) this.render();
    });
  }

  disconnectedCallback(): void {
    this.unsubscribe?.();
  }

  attributeChangedCallback(): void {
    if (this.isConnected) this.render();
  }

  private render(): void {
    const icon = getIcon(this.name);
    const size = this.getAttribute('size');
    if (size) {
      const value = /^\d+(\.\d+)?$/.test(size) ? `${size}px` : size;
      this.style.width = value;
      this.style.height = value;
    }
    const label = this.label;
    if (label) {
      this.setAttribute('role', 'img');
      this.setAttribute('aria-label', label);
      this.removeAttribute('aria-hidden');
    } else {
      this.removeAttribute('role');
      this.removeAttribute('aria-label');
      this.setAttribute('aria-hidden', 'true');
    }
    const stroke = this.getAttribute('stroke-width') ?? '2';
    const paint = icon?.filled
      ? 'fill="currentColor"'
      : `fill="none" stroke="currentColor" stroke-width="${stroke}" stroke-linecap="round" stroke-linejoin="round"`;
    createShadow(this, css, icon ? `<svg viewBox="${icon.viewBox ?? '0 0 24 24'}" ${paint} part="svg">${icon.body}</svg>` : '');
  }
}

define(HnIcon.tagName, HnIcon);
